import Navbar from 'components/navbar';
import { StagingPriority } from 'data/pps/mockData';
import { PrinterIcon } from 'lucide-react';
import { observer } from 'mobx-react';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Skeleton } from 'primereact/skeleton';
import { useState, useEffect } from 'react';
import classes from './stagingPriorityList.module.scss';
import { useStagingPriorityList } from './useStagingPriorityList';

const StagingPriorityList = () => {
  const { isLoading, stagingPriorityData, contentRef, getDateTime, handlePrint } =
    useStagingPriorityList();

  // State for current time to enable live color updates
  const [currentTime, setCurrentTime] = useState(new Date());

  // Update current time every 1 minute for live color updates
  useEffect(() => {
    const timeInterval = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000); // Update every 1 minute

    return () => clearInterval(timeInterval);
  }, []);

  // Function to parse job start time (format: "3-17-25 10:35 AM")
  const parseJobStartTime = (timeString: string): Date | null => {
    try {
      // Convert "3-17-25 10:35 AM" to a format JavaScript can parse
      const [datePart, timePart, ampm] = timeString.split(' ');
      const [month, day, year] = datePart.split('-');

      // Add 2000 to year if it's 2-digit
      const fullYear = year.length === 2 ? `20${year}` : year;

      // Create a proper date string: "MM/DD/YYYY HH:MM AM/PM"
      const formattedDateString = `${month}/${day}/${fullYear} ${timePart} ${ampm}`;

      return new Date(formattedDateString);
    } catch (error) {
      return null;
    }
  };

  // Function to get row class name based on job start time
  const getRowClassName = (rowData: StagingPriority): string => {
    if (!rowData.jobStartTime) {
      return '';
    }

    const jobDate = parseJobStartTime(rowData.jobStartTime);
    if (!jobDate || isNaN(jobDate.getTime())) {
      return '';
    }

    // Use the live currentTime state for real-time updates
    const timeDifferenceHours = (jobDate.getTime() - currentTime.getTime()) / (1000 * 60 * 60);

    if (timeDifferenceHours < 4) {
      if (timeDifferenceHours < 3) {
        return 'urgent-row';
      } else {
        return 'upcoming-row';
      }
    }

    // // Apply color conditions
    // if (timeDifferenceHours < 3) {
    //   return 'urgent-row'; // Red background
    // } else if (timeDifferenceHours < 4) {
    //   return 'upcoming-row'; // Orange background
    // }

    return '';
  };

  // Template for notes column to handle null values and show all notes separated by "/"
  const notesTemplate = (rowData: StagingPriority) => {
    if (!rowData.notes || !Array.isArray(rowData.notes) || rowData.notes.length === 0) {
      return '-';
    }

    // Join array of notes with "/"
    const formattedNotes = rowData.notes.join('/');

    return (
      <div className="notesContainer">
        <span>{formattedNotes}</span>
      </div>
    );
  };

  return (
    <>
      <Navbar heading="Staging Priority List" />
      <div className={classes.stagingPriorityContainer}>
        {isLoading ? (
          <Skeleton height="100rem" />
        ) : (
          <div id="content" className={classes.tableWrapper} ref={contentRef}>
            <div id="headerContainer" className={classes.headerContainer}>
              <div className={classes.dateContainer}>
                <p>
                  <span>Date: </span>
                  {getDateTime()}
                </p>
              </div>
              <div>
                <Button
                  id="print-btn"
                  icon={<PrinterIcon color="#4D2E6D" />}
                  rounded
                  style={{ backgroundColor: '#D6B1FD', border: 'none' }}
                  onClick={handlePrint}
                />
              </div>
            </div>
            <DataTable
              value={stagingPriorityData}
              responsiveLayout="scroll"
              stripedRows
              rowClassName={getRowClassName}
            >
              <Column
                header="#"
                body={(_, options) => options.rowIndex + 1}
                style={{ width: '5%', textAlign: 'center' }}
              />
              <Column field="jobs" header="Jobs" style={{ width: '12%' }} />
              <Column field="room" header="Room" style={{ width: '12%' }} />
              <Column field="description" header="Description" style={{ width: '15%' }} />
              <Column field="stageBy" header="Stage By" style={{ width: '12%' }} />
              <Column field="jobStartTime" header="Job Start Time" style={{ width: '10%' }} />
              <Column field="createdAt" header="Job Create / Edit Time" style={{ width: '10%' }} />
              <Column field="status" header="Status" style={{ width: '8%' }} />
              <Column field="notes" header="Notes" body={notesTemplate} style={{ width: '16%' }} />
            </DataTable>
          </div>
        )}
      </div>
    </>
  );
};

export default observer(StagingPriorityList);
